import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  ServiceChargeItem,
  TDCTransaction,
  TransactionModel,
  TransactionWrapper,
} from '@core/models';
import { ConnectionService } from '@features/pages/checkout/services/connection.service';
import { Client } from '@shared/models/client.model';
import { BehaviorSubject } from 'rxjs';
import { GroupLinkService } from './group-link.service';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  connectionService = inject(ConnectionService);
  router = inject(Router);
  groupLinkService = inject(GroupLinkService);
  transaction$: BehaviorSubject<TransactionWrapper | null> =
    new BehaviorSubject<TransactionWrapper | null>(null);

  hasRainoutInsurance(): boolean {
    return Boolean(
      this.transaction$.value?.transaction?.tdcTransaction?.hasInsuranceRainout
    );
  }

  getEventId(): number {
    return this.transaction$.value?.transaction?.eventId || 0;
  }

  getTotalSeats(): number {
    return this.transaction$.value?.transaction?.seats?.length || 0;
  }

  getTotalPrice(): number {
    return (
      this.transaction$.value?.transaction?.tdcTransaction?.totalPriceSeats || 0
    );
  }

  getUnitPrice(): number {
    if (!this.transaction$.value?.transaction?.seatsPrices) {
      return 0;
    }
    return (
      Object.values(this.transaction$.value?.transaction?.seatsPrices)?.[0]
        ?.value || 0
    );
  }

  getTransactionUpsell() {
    const buyerTypeId = this.transaction$.value?.transaction?.buyerTypeId;
    return this.transaction$.value?.transaction?.event?.upsells?.find(
      upsell => upsell.buyerTypeId === buyerTypeId
    );
  }

  getInsurancePrice(): number {
    return (
      this.transaction$.value?.transaction?.tdcTransaction
        ?.insuranceRainoutPrice || 0
    );
  }

  getServiceChargesDetails(
    t: TDCTransaction,
    client?: Client
  ): ServiceChargeItem[] {
    const pvCharges = t.serviceChargesDetails.pvServiceCharges;
    if (pvCharges) {
      return pvCharges;
    }

    const isFeeFromPv = client?.isFeeFromPv ?? false;

    if (!isFeeFromPv) {
      console.log('[ServiceCharges] Legacy mode', {
        client: client?.id,
        isFeeFromPv,
        cartBalance: t.balance,
        totalPriceSeats: t.totalPriceSeats,
        actualServiceCharges: t.balance - (t.totalPriceSeats ?? 0),
        insuranceRainoutPrice: t.insuranceRainoutPrice,
        clientServiceCharges: t.serviceChargesDetails.clientServiceCharges,
        rawCartCharges: Object.values(
          t.serviceChargesDetails.cartServiceCharges
        ).map(sc => ({
          code: sc.code,
          publicDescription: sc.publicDescription,
          type: sc.type,
          calculationType: sc.calculationType,
          total: sc.total,
          inclusiveCharge: sc.inclusiveCharge,
          amount: sc.amount,
          maxCapAmount: sc.maxCapAmount,
        })),
      });

      const calculatedServiceCharges = Object.values(
        t.serviceChargesDetails.clientServiceCharges ?? {}
      );

      const calculatedServiceChargesAmount = calculatedServiceCharges.reduce(
        (acc, item) => acc + (item?.total ?? 0),
        0
      );

      let actualServiceCharges = t.balance - (t.totalPriceSeats ?? 0);

      if (t.hasInsuranceRainout) {
        const actualServiceChargesWithoutInsurance =
          actualServiceCharges - (t.insuranceRainoutPrice ?? 0);
        actualServiceCharges =
          actualServiceChargesWithoutInsurance > 0
            ? actualServiceChargesWithoutInsurance
            : actualServiceCharges;
      }

      const hasNoConfiguredServiceCharges =
        calculatedServiceChargesAmount === 0;

      if (
        hasNoConfiguredServiceCharges ||
        calculatedServiceChargesAmount > actualServiceCharges
      ) {
        const cartServiceCharges = Object.values(
          t.serviceChargesDetails.cartServiceCharges
        ).filter(sc => sc.inclusiveCharge == false);

        const sc = cartServiceCharges.reduce<ServiceChargeItem[]>(
          (acc, item) => {
            const existing = acc.find(
              v => v.name === item.publicDescription
            );

            if (existing) {
              existing.amount += item.total;
            } else {
              acc.push({
                name: item.publicDescription,
                amount: item.total,
              });
            }

            return acc;
          },
          []
        );

        return sc;
      }

      const result = calculatedServiceCharges.map(item => {
        return { name: item.name, amount: item.total };
      });

      if (calculatedServiceChargesAmount < actualServiceCharges) {
        result.push({
          name: 'Others',
          amount: actualServiceCharges - calculatedServiceChargesAmount,
        });
      }

      return result;
    }

    const cartServiceCharges = Object.values(
      t.serviceChargesDetails.cartServiceCharges
    ).filter(sc => sc.inclusiveCharge === false);

    const grouped = cartServiceCharges.reduce<ServiceChargeItem[]>(
      (acc, item) => {
        const existing = acc.find(v => v.name === item.publicDescription);
        if (existing) {
          existing.amount += item.total;
        } else {
          acc.push({ name: item.publicDescription, amount: item.total });
        }
        return acc;
      },
      []
    );

    const actualServiceCharges = t.balance - (t.totalPriceSeats ?? 0);
    const insuranceAdjustment = t.hasInsuranceRainout
      ? (t.insuranceRainoutPrice ?? 0)
      : 0;
    const expectedTotal = actualServiceCharges - insuranceAdjustment;
    const calculatedTotal = grouped.reduce((sum, g) => sum + g.amount, 0);

    console.log('[ServiceCharges] Provenue Direct Mode', {
      client: client?.id,
      isFeeFromPv,
      cartBalance: t.balance,
      totalPriceSeats: t.totalPriceSeats,
      actualServiceCharges,
      insuranceRainoutPrice: t.insuranceRainoutPrice,
      expectedTotal,
      calculatedTotal,
      difference: expectedTotal - calculatedTotal,
      groupedCharges: grouped,
      rawCartCharges: cartServiceCharges.map(sc => ({
        code: sc.code,
        publicDescription: sc.publicDescription,
        type: sc.type,
        calculationType: sc.calculationType,
        total: sc.total,
        inclusiveCharge: sc.inclusiveCharge,
        amount: sc.amount,
        maxCapAmount: sc.maxCapAmount,
      })),
    });

    if (Math.abs(expectedTotal - calculatedTotal) > 0.01) {
      console.warn('[ServiceCharges] MISMATCH DETECTED', {
        expectedTotal,
        calculatedTotal,
        difference: expectedTotal - calculatedTotal,
      });
    }

    return grouped;
  }

  restartTransaction() {
    const transaction = this.transaction$.value;
    if (!transaction) {
      return;
    }
    const transactionId = transaction.transaction.id;
    this.connectionService
      .deleteTransaction(transactionId.toString())
      .subscribe({
        next: response => {
          this.goSeatSelection();
        },
        error: error => {
          // no need for a modal here
          this.goSeatSelection();
        },
      });
  }

  private goSeatSelection() {
    const transaction = this.transaction$.value;

    let eventId: number = 0;
    if (transaction) {
      eventId = (transaction.transaction as Required<TransactionModel>).event
        .pvEventId;
    }
    this.router.navigate(
      this.groupLinkService.prependGroupLink(['seat-selection']),
      {
        queryParams: { event: eventId, 'restore-cart': '1' },
      }
    );
  }
}
